import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, FileScan, ShieldCheck, UserPlus } from "lucide-react";
import { useApp } from "../lib/AppContext";

/**
 * Landing pubblica di Agenda Push.
 *
 * Bug 6/50 — pulita per l'utente anonimo:
 *  - un solo pulsante "Accedi" (rimosso il doppio Log-in header/hero)
 *  - link tecnici (OpenAPI, RLS, pipeline anteprima) NASCOSTI di default e
 *    accessibili solo con `?dev=1` (query param, no persistenza) — non
 *    rompe la homepage privata (Home.jsx è rotta separata)
 *  - CTA "Crea primo account" se `is_first_setup === true`
 */
export default function Landing() {
  const { config } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const devMode = params.get("dev") === "1";
  const name = config?.app_name || "Agenda Push";
  const isFirstSetup = !!config?.is_first_setup;

  return (
    <div className="min-h-dvh pxos-gradient-surface pxos-noise" data-testid="landing-page">
      <header className="mx-auto max-w-7xl px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={config?.brand_icon_url || "/branding/icon-192.png"}
            alt={name}
            className="h-10 w-10 rounded-full object-cover"
            style={{ background: "white", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
            data-testid="landing-brand-logo"
          />
          <div className="font-display text-xl font-semibold" style={{ color: "var(--pxos-text-dark)" }}>{name}</div>
        </div>
        {isFirstSetup ? (
          <button
            className="pxos-btn-primary inline-flex items-center gap-2"
            onClick={() => nav("/register")}
            data-testid="landing-first-setup-btn"
          >
            <UserPlus size={16} /> Crea primo account
          </button>
        ) : (
          <button
            className="pxos-btn-primary"
            onClick={() => nav("/login")}
            data-testid="landing-login-btn"
          >
            Accedi
          </button>
        )}
      </header>

      <section className="mx-auto max-w-7xl px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              style={{ color: "var(--pxos-text-dark)" }}>
            La tua agenda <em className="italic">PUSH</em>.<br />
            Organizza facilmente la tua vita.
          </h1>
          <p className="mt-6 text-lg leading-relaxed max-w-xl" style={{ color: "var(--pxos-text-dark)", opacity: 0.85 }}>
            Agenda, scadenze, finanze, documenti e CRM in un unico posto —
            con promemoria push, estrazione automatica dei dati da fatture e
            ricevute e revisione umana prima di ogni salvataggio.
          </p>

          {isFirstSetup && (
            <div
              className="mt-8 pxos-card p-5 border-l-4"
              style={{ borderLeftColor: "var(--pxos-primary-accent)" }}
              data-testid="landing-first-setup-banner"
            >
              <div className="text-xs font-mono uppercase tracking-widest opacity-70"
                   style={{ color: "var(--pxos-text-dark)" }}>Nuova installazione</div>
              <div className="mt-1 font-display text-lg font-semibold" style={{ color: "var(--pxos-text-dark)" }}>
                Non c'è ancora un account owner
              </div>
              <p className="mt-1 text-sm" style={{ color: "var(--pxos-text-dark)", opacity: 0.85 }}>
                Registra il primo account per iniziare a usare {name}. I collaboratori si aggiungono in seguito, su invito.
              </p>
              <button
                className="mt-4 pxos-btn-primary inline-flex items-center gap-2"
                onClick={() => nav("/register")}
                data-testid="landing-cta-first-setup"
              >
                <UserPlus size={16} /> Crea primo account
              </button>
            </div>
          )}

          {!isFirstSetup && (
            <div className="mt-8">
              <button
                className="pxos-btn-primary inline-flex items-center gap-2"
                onClick={() => nav("/login")}
                data-testid="landing-cta-primary"
              >
                Accedi <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>

        {devMode && (
          <div className="lg:col-span-5" data-testid="landing-dev-panel">
            <div className="pxos-card p-6 sm:p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-70"
                   style={{ color: "var(--pxos-text-dark)" }}>
                <FileScan size={14} /> Anteprima pipeline (dev)
              </div>
              <div className="mt-4 space-y-3 text-sm" style={{ color: "var(--pxos-text-dark)" }}>
                <StepRow n="1" label="Upload PDF o immagine — storage privato" />
                <StepRow n="2" label="pdfplumber → OCR Tesseract (fallback)" />
                <StepRow n="3" label="Estrazione strutturata via LLM (Claude)" />
                <StepRow n="4" label="Badge di confidenza + revisione umana" />
                <StepRow n="5" label="Conferma esplicita → audit log" />
              </div>
              <div className="mt-6 flex items-center gap-2 text-xs" style={{ color: "var(--pxos-text-dark)", opacity: 0.75 }}>
                <ShieldCheck size={14} /> RLS attiva. Nessun dato cross-tenant, nessun URL pubblico.
              </div>
              <div className="mt-4">
                <a
                  href="/api/openapi.json"
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border"
                  style={{ borderColor: "var(--pxos-border-strong)", color: "var(--pxos-text-dark)", background: "white" }}
                  data-testid="landing-openapi-link"
                >
                  OpenAPI
                </a>
              </div>
            </div>
          </div>
        )}
      </section>

      <footer className="mx-auto max-w-7xl px-6 pb-10 flex flex-wrap gap-4 text-xs" style={{ color: "var(--pxos-text-dark)", opacity: 0.7 }}>
        {config?.support_email && (
          <a href={`mailto:${config.support_email}`} className="underline" data-testid="landing-support-email">
            Contatti: {config.support_email}
          </a>
        )}
        <Link to="/privacy" className="underline" data-testid="landing-privacy-link">Privacy</Link>
        <Link to="/terms" className="underline" data-testid="landing-terms-link">Termini</Link>
      </footer>
    </div>
  );
}

function StepRow({ n, label }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="h-6 w-6 rounded-full grid place-items-center text-xs font-semibold shrink-0"
        style={{ background: "var(--pxos-primary-light)", color: "var(--pxos-text-dark)" }}
      >
        {n}
      </div>
      <span>{label}</span>
    </div>
  );
}
